// Purpose: (loosely) relates to Element types used within the ContentEditor - builds on Platform/ElementTypeUtils

// Imports from (only):
// ContentEditorSelectionUtils
// Constants
// types

import {
  findElementDescendant,
  getClosestRichTextElement,
  furthestTypographicElement,
  getTagFromData,
  contains,
  isUserComponent,
} from '@packages/utilities/platform';
import {
  isLineBreakElement,
  isFigure,
  isCodeBlock,
  isHtmlEmbed,
  isRichText,
  isPlainTextElement,
  isListItem,
  isFigCaption,
  isTypographicBlock,
  isLinkElement,
  isIframeElement,
  isImageElement,
  isEmphasized,
  isLink,
  isStrong,
  isSuperscript,
  isSubscript,
  isInlineCode,
  isList,
  isSpan,
  isStringElement,
  isBlockquote,
  isHeading,
  isParagraph,
} from '@packages/utilities/elementTypeUtils';
import {isTypographicCandidate} from '@packages/utilities/platform-elements';
import {
  Component,
  ExpressionPath,
  Expressions,
  Plugins,
} from '@packages/systems/wfdl';
import {
  EBoolean,
  EElement,
  EList,
  EText,
  Expression,
} from '@packages/systems/wfdl-ast';
import {qualify, QualifiedMap} from '@packages/systems/wfdl-Qualified';
import type {ItemRecordType as ItemType} from '@packages/systems/core/records/ItemRecord';

import {
  dataTypesFromComponents,
  filterDivergentRichTextNodes,
  reifyElementTree,
} from '@packages/systems/core/render';
import {rteHtmlToJsonNodes} from '@packages/systems/core/utils/JsonDomUtils';
import type {ExpressionElement} from '@packages/systems/wfdl-ast';
import {FieldType} from '@packages/systems/dynamo/records/Field';
import type {RichTextValue} from '@packages/systems/dynamo/binding-props';
import type {JSONNode} from '@packages/shared/types/JSONNode';
import {getDynBindingsFromElement} from '@packages/systems/dynamo/utils/DynamoElementUtils';
import {RangeSelectionRecord} from '@packages/shared/records';
import {ContentEditorActive} from '@packages/shared/states';
import type {FormatToolbarButtonsType} from './constants';
import {formatNumberWithConstraints} from '@packages/systems/core/utils/NumberUtils';
import {
  assertRangeSelectionRecordType,
  getSelectedElements,
  findLeftElement,
  findRightElement,
  isStringOrLineBreak,
  isCollapsed,
} from './ContentEditorSelectionUtils';
import {
  extractFieldSlugFromUniversalBinding,
  isNumberUniversalBinding,
} from '@packages/shared/render/utils/ContentEditorUtils';

// so we do not have to do the null check every time we get the val of am Expression we know is an Element
export const contentEditorGetElement = (
  expr: Expression
): ExpressionElement => {
  const element = Expressions.getElement(expr);
  if (element === null) {
    throw Error('Element not found');
  }
  return element;
};

// returns image within image figure, iframe within video figure. In rich text, the nesting of these elements is static.
export const findDeepFigureChildElement = (
  subtree: ExpressionElement,
  elementId: string
): ExpressionElement | null => {
  const figureNodeToSearch = Expressions.getElementById(
    EElement(subtree),
    elementId
  );
  if (figureNodeToSearch === null) return null;
  return (
    findElementDescendant(
      figureNodeToSearch,
      (el) => isImageElement(el) || isIframeElement(el)
    ) || null
  );
};

const findDescendant =
  (finder: (el: ExpressionElement) => ExpressionElement | null) =>
  (root: ExpressionElement, elementId: string) => {
    const elToSearch = Expressions.getElementById(EElement(root), elementId);
    if (elToSearch === null) return null;
    return finder(elToSearch);
  };

export const getLeftDeepestTextOrLineBreak = findDescendant(
  findLeftElement(isStringOrLineBreak)
);

export const getRightDeepestTextOrLineBreak = findDescendant(
  findRightElement(isStringOrLineBreak)
);

export const getLeftDeepestTextNode = findDescendant(
  findLeftElement(isStringElement)
);

export const getRightDeepestTextNode = findDescendant(
  findRightElement(isStringElement)
);

export const getLeftDeepLink = findDescendant(findLeftElement(isLinkElement));

export const selectTextElement =
  (linkId: string) => (state: ContentEditorActive) => {
    const {subtree} = state;
    const leftNode = getLeftDeepestTextNode(subtree, linkId);
    const rightNode = getRightDeepestTextNode(subtree, linkId);

    if (!leftNode || !rightNode) return state;

    const newSelection = RangeSelectionRecord({
      anchorNodeId: leftNode.id,
      focusNodeId: rightNode.id,
      anchorOffset: 0,
      focusOffset: (Expressions.getText(rightNode.data) ?? '').length,
      leftToRight: true,
    });

    return state.set('selection', newSelection);
  };

// UTILS FOR CMS WYSIWYG INITATIVE
// @TODO - Determine where these should live
const CHILDREN_PATH: ExpressionPath = [{in: 'Record', at: 'children'}];
const stringType = qualify(Plugins.name('Basic'), Component.name('String'));

// can also be used as a source of truth for whether the element has a type of binding on canvas editing supports. Should be used with `isDynamicallyBoundWithComposableCMSFeatureFlag` to ensure it is not from an unsupported CMS collection  i.e. ECommerce.
export const getFieldSlugInChildrenBinding = (
  element: ExpressionElement
): string | null => {
  const dataExpr = Expressions.getElementData(element);
  const childrenExpr = Expressions.getIn(dataExpr, CHILDREN_PATH);

  if (!childrenExpr) return null;

  // Try to extract field slug using the shared universal binding logic
  const universalBindingSlug =
    extractFieldSlugFromUniversalBinding(childrenExpr);
  if (universalBindingSlug) {
    return universalBindingSlug;
  }

  // Legacy Bindings (ie. Link)
  const legacyBinding = getDynBindingsFromElement(element)['innerHTML'];

  if (Array.isArray(legacyBinding) && legacyBinding.length) {
    const [binding] = legacyBinding;
    if (
      binding &&
      (binding.type === 'PlainText' || binding.type === 'Number')
    ) {
      return binding.slug || null;
    }
  }
  return null;
};

/**
 * Checks if an element's children binding is bound to a Number CMS field.
 * Handles both universal bindings and legacy bindings.
 * Used to exclude Number fields from GenAI options.
 */
export const hasNumberFieldChildrenBinding = (
  element: ExpressionElement
): boolean => {
  const dataExpr = Expressions.getElementData(element);
  const childrenExpr = Expressions.getIn(dataExpr, CHILDREN_PATH);

  // Check universal bindings
  if (childrenExpr && isNumberUniversalBinding(childrenExpr)) {
    return true;
  }

  // Check legacy bindings
  const legacyBinding = getDynBindingsFromElement(element)['innerHTML'];
  if (Array.isArray(legacyBinding) && legacyBinding.length) {
    const [binding] = legacyBinding;
    if (binding?.type === 'Number') {
      return true;
    }
  }

  return false;
};

export const setStringElementOnChildren = (
  element: ExpressionElement,
  id: string,
  textValue: string
): ExpressionElement => {
  const dataExpr = Expressions.getElementData(element);
  const stringElement = EElement({
    id,
    type: stringType,
    data: EText(textValue),
  });

  return Expressions.setElementData(
    element,
    Expressions.setIn(dataExpr, CHILDREN_PATH, EList([stringElement]))
  );
};

export const setRichTextContentOnChildren = (
  element: ExpressionElement,
  jsonContentNodes: JSONNode[],
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType
): ExpressionElement => {
  const dataTypes = dataTypesFromComponents(plugins, componentMap);
  const {jsonContentNodes: filteredJsonContentNodes} =
    filterDivergentRichTextNodes(plugins, dataTypes, jsonContentNodes);
  const elements = reifyElementTree(
    filteredJsonContentNodes,
    componentMap,
    plugins
  );
  const dataExpr = Expressions.getElementData(element);

  return Expressions.setElementData(
    element,
    Expressions.setIn(dataExpr, CHILDREN_PATH, EList(elements.map(EElement)))
  );
};

export const createSubtreeFromItem = (
  seedId: string,
  field: FieldType,
  element: ExpressionElement,
  collectionItem: ItemType,
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType,
  enforceRichTextTagStructure: (
    subtree: ExpressionElement,
    seedId: string
  ) => ExpressionElement
): ExpressionElement => {
  const fieldType = field.get('type');
  const isPlainText = fieldType === 'PlainText';
  const isRichTextField = fieldType === 'RichText';
  const isNumberField = fieldType === 'Number';

  if (isPlainText) {
    const textValue = collectionItem.get(field.get('slug')) || '';
    return setStringElementOnChildren(element, seedId, textValue);
  }

  if (isNumberField) {
    const numberValue = collectionItem.get(field.get('slug'));
    const validations = field.get('validations');

    // Format the number value according to field validations
    let formattedValue = '';
    if (typeof numberValue === 'number') {
      const format = validations?.get('format') || 'decimal';
      const precision = validations?.get('precision');
      const minValue = validations?.get('minValue');
      const maxValue = validations?.get('maxValue');
      const allowNegative = validations?.get('allowNegative');

      formattedValue = formatNumberWithConstraints(numberValue, {
        format,
        precision,
        minValue,
        maxValue,
        allowNegative,
      });
    }

    return setStringElementOnChildren(element, seedId, formattedValue);
  }

  if (isRichTextField) {
    let jsonContentNodes = (collectionItem.get(field.get('slug')) ||
      []) as RichTextValue;

    let wasHtmlString = false;

    if (!Array.isArray(jsonContentNodes)) {
      if (typeof jsonContentNodes === 'string') {
        wasHtmlString = true;
        jsonContentNodes = rteHtmlToJsonNodes(jsonContentNodes, {
          allowDynLink: true,
          supportHtmlEmbeds: true,
          seedId,
        }).jsonContentNodes;
      } else {
        jsonContentNodes = jsonContentNodes.toJS() as JSONNode[];
      }
    }

    const result = setRichTextContentOnChildren(
      element,
      jsonContentNodes,
      componentMap,
      plugins
    );

    // HTML string input (e.g. from the Public API) may not conform to the
    // tag structure expected by the content editor. Enforce before editing.
    if (wasHtmlString) {
      return enforceRichTextTagStructure(result, seedId);
    }

    return result;
  }

  return element;
};

export const isChildrenDynamicallyBoundToItem = (
  editTargetElement: ExpressionElement | null,
  itemFieldSlugDerivedFromBinding: string | null,
  isPageBranch: boolean
): boolean =>
  Boolean(
    itemFieldSlugDerivedFromBinding && !isPageBranch && editTargetElement
  );

// gets the typography element to be used in the ContentEditor. Also returns the fieldSlug of the CMS binding if the element is bound.
export const getEditTargetElement = (
  element: ExpressionElement | null,
  currentComponentRender: Expression,
  {
    allowTypographicConversion = true,
  }: {allowTypographicConversion?: boolean} = {}
): {editTargetElement: ExpressionElement | null; fieldSlug: string | null} => {
  if (!element)
    return {
      editTargetElement: null,
      fieldSlug: null,
    };

  const closestRichText = getClosestRichTextElement(
    currentComponentRender,
    element.id
  );

  // select rich text if the selected element is within a rich text
  if (closestRichText) {
    return {
      editTargetElement: closestRichText,
      fieldSlug: getFieldSlugInChildrenBinding(closestRichText),
    };
  }

  // change the element data to reflect that it can be edited as a text element if it isn't already.
  if (
    isTypographicCandidate(element, {
      allowTypographicConversion,
    })
  ) {
    return {
      editTargetElement: Expressions.setElementData(
        element,
        Expressions.setIn(
          Expressions.getElementData(element),
          [{in: 'Record', at: 'text'}],
          EBoolean(true)
        )
      ),
      fieldSlug: null,
    };
  }

  const fieldSlug = getFieldSlugInChildrenBinding(element);

  // if the element is bound to a CMS field, set the element data to reflect that it can be edited as a text element and return fieldSlug
  if (fieldSlug) {
    return {
      editTargetElement: Expressions.setElementData(
        element,
        Expressions.setIn(
          Expressions.getElementData(element),
          [{in: 'Record', at: 'text'}],
          EBoolean(true)
        )
      ),
      fieldSlug,
    };
  }

  // otherwise, return the top level typographic element that this element is nested within (e.g. this could be a string within a bold element within a paragraph).
  return {
    editTargetElement:
      furthestTypographicElement(element, currentComponentRender) || null,
    fieldSlug: null,
  };
};

export const linkType = qualify(Plugins.name('Basic'), Component.name('Link'));
export const spanType = qualify(Plugins.name('Basic'), Component.name('Span'));
export const strongType = qualify(
  Plugins.name('Basic'),
  Component.name('Strong')
);
export const emphasizedType = qualify(
  Plugins.name('Basic'),
  Component.name('Emphasized')
);
export const superscriptType = qualify(
  Plugins.name('Basic'),
  Component.name('Superscript')
);
export const subscriptType = qualify(
  Plugins.name('Basic'),
  Component.name('Subscript')
);
export const inlineCodeType = qualify(
  Plugins.name('Basic'),
  Component.name('InlineCode')
);
export const headingType = qualify(
  Plugins.name('Basic'),
  Component.name('Heading')
);
export const blockquoteType = qualify(
  Plugins.name('Basic'),
  Component.name('Blockquote')
);
export const paragraphType = qualify(
  Plugins.name('Basic'),
  Component.name('Paragraph')
);

const emptyQualifiedMap = new QualifiedMap<Component.ComponentName, true>();
const FORMAT_ELEMENT_TYPES = emptyQualifiedMap
  .set(linkType, true)
  .set(spanType, true)
  .set(strongType, true)
  .set(emphasizedType, true)
  .set(superscriptType, true)
  .set(subscriptType, true)
  .set(inlineCodeType, true);

const FORMAT_OR_TEXT_ELEMENT_TYPES = FORMAT_ELEMENT_TYPES.set(
  qualify(Plugins.name('Basic'), Component.name('String')),
  true
);

const TOP_LEVEL_RICH_TEXT_TYPOGRAPHIC_ELEMENT_TYPES = emptyQualifiedMap
  .set(qualify(Plugins.name('Basic'), Component.name('Paragraph')), true)
  .set(headingType, true)
  .set(blockquoteType, true);

export function isFormatElement(element: ExpressionElement): boolean {
  return FORMAT_ELEMENT_TYPES.has(element.type);
}

export function isFormatOrTextElement(element: ExpressionElement): boolean {
  return FORMAT_OR_TEXT_ELEMENT_TYPES.has(element.type);
}

export const isRichTextTopLevelTypographic = (
  element: ExpressionElement
): boolean => {
  return TOP_LEVEL_RICH_TEXT_TYPOGRAPHIC_ELEMENT_TYPES.has(element.type);
};

const NON_FORMAT_RICH_TEXT_TYPOGRAPHIC_ELEMENT_TYPES =
  TOP_LEVEL_RICH_TEXT_TYPOGRAPHIC_ELEMENT_TYPES.set(
    qualify(Plugins.name('Basic'), Component.name('ListItem')),
    true
  ).set(qualify(Plugins.name('Basic'), Component.name('Figcaption')), true);

export function isRichTextNonFormatTypographic(element: ExpressionElement) {
  return NON_FORMAT_RICH_TEXT_TYPOGRAPHIC_ELEMENT_TYPES.has(element.type);
}

const SIMPLE_TEXT_ELEMENT_TYPES = FORMAT_ELEMENT_TYPES.set(
  qualify(Plugins.name('Basic'), Component.name('LineBreak')),
  true
);

export function isSimpleTextElement(element: ExpressionElement) {
  return SIMPLE_TEXT_ELEMENT_TYPES.has(element.type);
}

const VALID_TEXT_ELEMENT_PARENT_TYPES =
  NON_FORMAT_RICH_TEXT_TYPOGRAPHIC_ELEMENT_TYPES.union(FORMAT_ELEMENT_TYPES);

export function isValidTextElementParent(element: ExpressionElement) {
  return VALID_TEXT_ELEMENT_PARENT_TYPES.has(element.type);
}

const VALID_FIGCAPTION_FORMAT_ELEMENT_TYPES = emptyQualifiedMap
  .set(linkType, true)
  .set(strongType, true)
  .set(emphasizedType, true)
  .set(qualify(Plugins.name('Basic'), Component.name('LineBreak')), true);

export function isFigcaptionFormatElement(element: ExpressionElement) {
  return VALID_FIGCAPTION_FORMAT_ELEMENT_TYPES.has(element.type);
}

const UNMERGEABLE_ELEMENT_TYPES = emptyQualifiedMap
  .set(qualify(Plugins.name('Basic'), Component.name('Figure')), true)
  .set(qualify(Plugins.name('Basic'), Component.name('Block')), true);

export function isUnmergeableElement(element: ExpressionElement) {
  return UNMERGEABLE_ELEMENT_TYPES.has(element.type);
}

const validSpanParents = NON_FORMAT_RICH_TEXT_TYPOGRAPHIC_ELEMENT_TYPES.set(
  linkType,
  true
);

const validStrongParents = validSpanParents.set(spanType, true);

const validEmphasizedParents = validStrongParents.set(
  qualify(Plugins.name('Basic'), Component.name('Strong')),
  true
);

const validSuperAndSubscriptParents = validEmphasizedParents
  .set(inlineCodeType, true)
  .set(emphasizedType, true);

const validInlineCodeParents = validEmphasizedParents
  .set(superscriptType, true)
  .set(subscriptType, true)
  .set(emphasizedType, true);

const VALID_PARENT_ELEMENT_TYPES = new QualifiedMap<
  Component.ComponentName,
  QualifiedMap<Component.ComponentName, true>
>({
  Basic: {
    Link: NON_FORMAT_RICH_TEXT_TYPOGRAPHIC_ELEMENT_TYPES.set(
      qualify(Plugins.name('Basic'), Component.name('Figure')),
      true
    ),
    Block: emptyQualifiedMap
      .set(linkType, true)
      .set(qualify(Plugins.name('Basic'), Component.name('Figure')), true),
    Image: emptyQualifiedMap.set(
      qualify(Plugins.name('Basic'), Component.name('Block')),
      true
    ),
    Iframe: emptyQualifiedMap.set(
      qualify(Plugins.name('Basic'), Component.name('Block')),
      true
    ),
    Figcaption: emptyQualifiedMap.set(
      qualify(Plugins.name('Basic'), Component.name('Figure')),
      true
    ),
    ListItem: emptyQualifiedMap.set(
      qualify(Plugins.name('Basic'), Component.name('List')),
      true
    ),
    List: emptyQualifiedMap.set(
      qualify(Plugins.name('Basic'), Component.name('ListItem')),
      true
    ),
    Span: validSpanParents,
    Strong: validStrongParents,
    Emphasized: validEmphasizedParents,
    // Superscript and subscript are mutually exclusive in the toolbar but either
    // may be stored inside the other (e.g. API-created rich text), so each is a
    // valid parent of the other. Without this, raising either out of the other
    // only inverts the nesting and hierarchy enforcement never converges, which
    // hangs the editor when such an item is opened.
    // Apply this per entry so same-type nesting stays invalid and collapses.
    Superscript: validSuperAndSubscriptParents.set(subscriptType, true),
    Subscript: validSuperAndSubscriptParents.set(superscriptType, true),
    LineBreak:
      NON_FORMAT_RICH_TEXT_TYPOGRAPHIC_ELEMENT_TYPES.union(
        FORMAT_ELEMENT_TYPES
      ),
    InlineCode: validInlineCodeParents,
  },
});

export function isValidParent(
  element: ExpressionElement,
  parent: ExpressionElement
) {
  const validParentTypes = VALID_PARENT_ELEMENT_TYPES.get(element.type);
  if (validParentTypes?.has(parent.type)) return true;
  return false;
}

const isRichTextDirectChild = (
  subtree: ExpressionElement,
  elementId: string
): boolean => {
  const parent = Expressions.getParent(EElement(subtree), elementId);

  if (!parent) return false;

  return isRichText(parent);
};

const isNonNestedList = (
  subtree: ExpressionElement,
  element: ExpressionElement
): boolean => {
  return isList(element) && isRichTextDirectChild(subtree, element.id);
};

export const isRichTextTopLevelElement =
  (subtree: ExpressionElement) =>
  (element: ExpressionElement): boolean => {
    return (
      isRichTextTopLevelTypographic(element) ||
      isFigure(element) ||
      isNonNestedList(subtree, element) ||
      isCodeBlock(element) ||
      isHtmlEmbed(element) ||
      isUserComponent(element.type)
    );
  };

export const isPotentialRichTextTopLevelElement = (
  element: ExpressionElement
): boolean => {
  return (
    isRichTextTopLevelTypographic(element) ||
    isFigure(element) ||
    isList(element) ||
    isCodeBlock(element) ||
    isHtmlEmbed(element) ||
    isUserComponent(element.type)
  );
};

export const isNonFormatTypographic = (element: ExpressionElement) => {
  return (
    isParagraph(element) ||
    isHeading(element) ||
    isBlockquote(element) ||
    isListItem(element) ||
    isFigCaption(element) ||
    isTypographicBlock(element)
  );
};

// lists the elements 1) we do not send to the LLM 2) do not filter out from the LLM result
export const isAiGenerationSupportedElement = (el: ExpressionElement) => {
  return (
    isRichTextTopLevelTypographic(el) ||
    // <br> is excluded because it prevents the next be selected if the <br> is the first or last element
    (isPlainTextElement(el) && !isLineBreakElement(el)) ||
    isLinkElement(el) ||
    isListItem(el) ||
    isList(el) ||
    isCodeBlock(el)
  );
};

export const getClosestTopLevelElement = (
  elementId: string,
  subtree: ExpressionElement
): ExpressionElement | null => {
  return Expressions.closestElement(
    EElement(subtree),
    elementId,
    isRichTextTopLevelElement(subtree)
  );
};

export const getClosestTopLevelTypoElement = (
  elementId: string,
  subtree: ExpressionElement
): ExpressionElement | null => {
  return Expressions.closestElement(
    EElement(subtree),
    elementId,
    isRichTextTopLevelTypographic
  );
};

type FormatTypeChecker = (element: ExpressionElement) => boolean;

export const isToolbarFormatType: {
  [Property in keyof FormatToolbarButtonsType]: FormatTypeChecker;
} = {
  bold: isStrong,
  italic: isEmphasized,
  link: isLink,
  span: isSpan,
  blockquote: isBlockquote,
  superscript: isSuperscript,
  subscript: isSubscript,
  inlineCode: isInlineCode,
  clear: () => false,
  h1: (element) => isHeading(element) && getTagFromData(element) === 'h1',
  h2: (element) => isHeading(element) && getTagFromData(element) === 'h2',
  h3: (element) => isHeading(element) && getTagFromData(element) === 'h3',
  h4: (element) => isHeading(element) && getTagFromData(element) === 'h4',
  h5: (element) => isHeading(element) && getTagFromData(element) === 'h5',
  h6: (element) => isHeading(element) && getTagFromData(element) === 'h6',
  ol: (element) => isList(element) && getTagFromData(element) === 'ol',
  ul: (element) => isList(element) && getTagFromData(element) === 'ul',
};

export const isLinkEditingAllowed = (
  contentEditorState: ContentEditorActive
) => {
  return (
    canHaveLinksInContent(contentEditorState) &&
    canCreateLinkFromSelection(contentEditorState)
  );
};

export const canHaveLinksInContent = (
  contentEditorState: ContentEditorActive
) => {
  const {subtree, nestedInAnchor} = contentEditorState;
  const isAnchor = isLink(subtree);

  return !nestedInAnchor && !isAnchor;
};

// checks if both edges of selection are within same typographic element
const hasCommonClosestTypographicAncestor = (
  selectedElements: ExpressionElement[],
  anchorNodeId: string,
  focusNodeId: string
): boolean => {
  let currentTypographicAncestorId: string | null = null;
  let closestTypoAnchorAncestorId: string | null = null;
  let closestTypoFocusAncestorId: string | null = null;

  for (const element of selectedElements) {
    // Update current typographic context
    if (isNonFormatTypographic(element)) {
      currentTypographicAncestorId = element.id;
    }

    if (currentTypographicAncestorId) {
      // Case: anchor *is* the typographic node (because it between two linebreak and the selection is the parent)
      if (currentTypographicAncestorId === anchorNodeId) {
        closestTypoAnchorAncestorId = currentTypographicAncestorId;

        // Focus is also the same node
        if (
          focusNodeId === anchorNodeId ||
          // Focus is a child of anchor
          contains(element, (el) => el.id === focusNodeId)
        ) {
          closestTypoFocusAncestorId = currentTypographicAncestorId;
          break;
        }

        break; // false case - Anchor is the typographic, but focus isn't under it
      }

      // Case: focus *is* the typographic node (because it between two linebreak and the selection is the parent)
      if (currentTypographicAncestorId === focusNodeId) {
        closestTypoFocusAncestorId = currentTypographicAncestorId;

        if (
          anchorNodeId === focusNodeId ||
          contains(element, (el) => el.id === anchorNodeId)
        ) {
          closestTypoAnchorAncestorId = currentTypographicAncestorId;
          break;
        }

        break; // false case - Focus is the typographic, but anchor isn't under it
      }
    }

    // at this point we know they are text nodes because weren't any currentTypographicAncestorId above it
    if (element.id === anchorNodeId) {
      closestTypoAnchorAncestorId = currentTypographicAncestorId;
    }

    if (element.id === focusNodeId) {
      closestTypoFocusAncestorId = currentTypographicAncestorId;
    }

    // true case - found both so stop early
    if (closestTypoAnchorAncestorId && closestTypoFocusAncestorId) {
      break;
    }
  }

  return Boolean(
    closestTypoAnchorAncestorId &&
    closestTypoFocusAncestorId &&
    closestTypoAnchorAncestorId === closestTypoFocusAncestorId
  );
};

const canCreateLinkFromSelection = (
  contentEditorState: ContentEditorActive
) => {
  const {subtree} = contentEditorState;

  if (isRichText(subtree)) {
    const {selection} = contentEditorState;
    // When editing rich text, only allow adding a link if there is a selection
    // and if that selection is within the bounds of a single non-format-typo node
    // ie: p, h1-6, blockquote, li, figcaption
    const rangeSelection = assertRangeSelectionRecordType(selection);
    if (rangeSelection?.anchorNodeId && !isCollapsed(rangeSelection)) {
      const selectedNodes = getSelectedElements(subtree, rangeSelection);
      return (
        selectedNodes.some((element) => isLink(element)) ||
        hasCommonClosestTypographicAncestor(
          selectedNodes,
          rangeSelection.anchorNodeId,
          rangeSelection.focusNodeId
        )
      );
    } else {
      return false;
    }
  } else {
    return true;
  }
};
